import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, KeyRound, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * ForgotPasswordDialog — modale per richiedere il reset della password.
 * Il backend invia una password temporanea all'email associata e setta il flag
 * must_change_password=True, che forzerà il cambio al primo accesso.
 */
export default function ForgotPasswordDialog({ open, onOpenChange }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!email.trim()) {
      setError("Inserisci l'email associata al tuo account");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    onOpenChange?.(false);
    setTimeout(() => {
      setDone(false);
      setEmail("");
      setError("");
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="forgot-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-blue-600" />
            Hai dimenticato la password?
          </DialogTitle>
          <DialogDescription>
            Inserisci l&apos;email associata al tuo account. Riceverai una password
            temporanea da cambiare al primo accesso.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="py-6 text-center" data-testid="forgot-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
            <h3 className="font-heading font-semibold text-lg mt-3">Controlla la tua email</h3>
            <p className="text-sm text-slate-600 mt-2">
              Se l&apos;email è associata a un account, ti abbiamo inviato una password
              temporanea. Controlla anche la cartella spam.
            </p>
            <Button onClick={close} className="mt-5" data-testid="forgot-close">Chiudi</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Email *</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tua@email.it"
                autoFocus
                required
                data-testid="forgot-email"
              />
            </div>
            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="forgot-error">
                {error}
              </div>
            )}
            <DialogFooter className="gap-2 sm:gap-2">
              <Button type="button" variant="outline" onClick={close} data-testid="forgot-cancel">
                Annulla
              </Button>
              <Button type="submit" disabled={submitting} data-testid="forgot-submit">
                <Send className="w-4 h-4 mr-2" />
                {submitting ? "Invio..." : "Invia password"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
